import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { EOrqColor, OrqIconContainer } from "@orquestra-web/react";

import styles from "./index.module.css";
import { ReactMarkdown } from "react-markdown/lib/react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import sidebarItems from "./sidebar_items.json";

const SIDEBAR_DEFAULT_WIDTH = 340;
const SIDEBAR_MIN_WIDTH = SIDEBAR_DEFAULT_WIDTH / 2;
const SIDEBAR_COLLAPSE_TRIGGER_WIDTH = SIDEBAR_MIN_WIDTH - 48;
const SIDEBAR_COLLAPSED_WIDTH = 0;
const SIDEBAR_MAX_WIDTH = 520;
const MAIN_SITE_URL = "https://dataservices.btgpactual.com/site";
const MOBILE_NAVIGATION_REVEAL_MS = 2600;
const MOBILE_NAVIGATION_HIDE_MS = 1800;

const isSidebarCollapsed = (width: number) => width <= SIDEBAR_COLLAPSED_WIDTH;

const clampSidebarWidth = (width: number) => {
  if (width <= SIDEBAR_COLLAPSE_TRIGGER_WIDTH) {
    return SIDEBAR_COLLAPSED_WIDTH;
  }

  if (width < SIDEBAR_MIN_WIDTH) {
    return SIDEBAR_MIN_WIDTH;
  }

  return Math.min(SIDEBAR_MAX_WIDTH, width);
};

const getNextSidebarKeyboardWidth = (currentWidth: number, delta: number) => {
  if (isSidebarCollapsed(currentWidth) && delta > 0) {
    return SIDEBAR_MIN_WIDTH;
  }

  if (currentWidth <= SIDEBAR_MIN_WIDTH && delta < 0) {
    return SIDEBAR_COLLAPSED_WIDTH;
  }

  return clampSidebarWidth(currentWidth + delta);
};

type SidebarSubitem = {
  name: string;
  href?: string;
};

type SidebarItem = SidebarSubitem & {
  subitems: SidebarSubitem[];
};

type SidebarSection = {
  id: string;
  parentId: string;
};

const formatID = (title: string) => {
  return title.toLowerCase().split(" ").join("-");
};

const getHrefId = (href?: string) => href?.replace(/^#/, "") || "";

const getHeadingText = (children: React.ReactNode) => {
  return React.Children.toArray(children)
    .map((child) =>
      typeof child === "string" || typeof child === "number" ? child : ""
    )
    .join("")
    .trim();
};

const SIDEBAR_LOGO_VISIBLE_ATTRIBUTE = "data-sidebar-logo-visible";
const MIN_SIDEBAR_CONTENT_HEIGHT = 280;
const getHeaderOffset = () =>
  window.matchMedia("(max-width: 1120px)").matches ? 70 : 90;

const getPageScrollRoot = () =>
  (document.scrollingElement as HTMLElement | null) || document.documentElement;

const isMobileViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(max-width: 980px)").matches;

const useMobileNavigationReveal = (isMobileNavigationOpen: boolean) => {
  const [isVisible, setIsVisible] = useState(false);
  const hideTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const clearHideTimeout = () => {
      if (hideTimeoutRef.current !== null) {
        window.clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };

    const hideSoon = (delay = MOBILE_NAVIGATION_HIDE_MS) => {
      clearHideTimeout();
      hideTimeoutRef.current = window.setTimeout(() => {
        setIsVisible(false);
      }, delay);
    };

    const reveal = () => {
      if (!isMobileViewport() || isMobileNavigationOpen) return;

      clearHideTimeout();
      setIsVisible(true);
    };

    const revealTemporarily = () => {
      reveal();
      if (!isMobileViewport() || isMobileNavigationOpen) return;

      hideSoon(MOBILE_NAVIGATION_REVEAL_MS);
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === "mouse") return;
      reveal();
    };
    const handlePointerUp = (event: PointerEvent) => {
      if (event.pointerType === "mouse") return;
      hideSoon();
    };
    const handleTouchStart = () => reveal();
    const handleTouchEnd = () => hideSoon();
    const handleClick = () => revealTemporarily();
    const handleResize = () => {
      if (!isMobileViewport()) {
        clearHideTimeout();
        setIsVisible(false);
      }
    };

    window.addEventListener("pointerdown", handlePointerDown, { passive: true });
    window.addEventListener("pointerup", handlePointerUp, { passive: true });
    window.addEventListener("pointercancel", handlePointerUp, { passive: true });
    window.addEventListener("touchstart", handleTouchStart, { passive: true });
    window.addEventListener("touchend", handleTouchEnd, { passive: true });
    window.addEventListener("touchcancel", handleTouchEnd, { passive: true });
    window.addEventListener("click", handleClick, true);
    window.addEventListener("resize", handleResize);

    return () => {
      clearHideTimeout();
      window.removeEventListener("pointerdown", handlePointerDown);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
      window.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("touchcancel", handleTouchEnd);
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [isMobileNavigationOpen]);

  useEffect(() => {
    if (isMobileNavigationOpen) {
      setIsVisible(false);
    }
  }, [isMobileNavigationOpen]);

  return isVisible && !isMobileNavigationOpen;
};

const updateSidebarLogoVisibility = (
  sidebar: HTMLElement | null,
  logoSelector: string
) => {
  if (!sidebar) return;

  const logoLink = sidebar.querySelector<HTMLElement>(logoSelector);

  if (!logoLink) return;

  sidebar.removeAttribute(SIDEBAR_LOGO_VISIBLE_ATTRIBUTE);

  const previousDisplay = logoLink.style.display;
  const previousVisibility = logoLink.style.visibility;

  logoLink.style.display = "flex";
  logoLink.style.visibility = "hidden";
  const logoHeight = logoLink.getBoundingClientRect().height;

  logoLink.style.display = previousDisplay;
  logoLink.style.visibility = previousVisibility;

  sidebar.setAttribute(
    SIDEBAR_LOGO_VISIBLE_ATTRIBUTE,
    sidebar.clientHeight - logoHeight >= MIN_SIDEBAR_CONTENT_HEIGHT
      ? "true"
      : "false"
  );
};

const Home: React.FC = () => {
  const logoBasePath = process.env.REACT_APP_PATH_HOME || "/home/assets";
  const [markdownContent, setMarkdownContent] = useState("");
  const [activeHref, setActiveHref] = useState("");
  const [showTopLink, setShowTopLink] = useState(false);
  const [expandedSubmenus, setExpandedSubmenus] = useState<Set<string>>(
    () => new Set()
  );
  const [sidebarWidth, setSidebarWidth] = useState(SIDEBAR_DEFAULT_WIDTH);
  const [isResizingSidebar, setIsResizingSidebar] = useState(false);
  const [isMobileNavigationOpen, setIsMobileNavigationOpen] = useState(false);
  const isMobileNavigationToggleVisible =
    useMobileNavigationReveal(isMobileNavigationOpen);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sideNavRef = useRef<HTMLDivElement | null>(null);
  const mainContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollSpyAnimationFrameRef = useRef<number | null>(null);
  const pendingNavigationRef = useRef<string | null>(null);
  const pendingNavigationTimeoutRef = useRef<number | null>(null);
  const sidebarSections = useMemo<SidebarSection[]>(() => {
    const sections: SidebarSection[] = [];

    (sidebarItems.items as SidebarItem[]).forEach((item) => {
      const itemId = getHrefId(item.href);

      if (!itemId) return;

      sections.push({ id: itemId, parentId: itemId });

      item.subitems.forEach((subitem) => {
        const subitemId = getHrefId(subitem.href);

        if (subitemId) {
          sections.push({ id: subitemId, parentId: itemId });
        }
      });
    });

    return sections;
  }, []);
  const activeParentId = useMemo(() => {
    return (
      sidebarSections.find(({ id }) => id === activeHref)?.parentId ||
      activeHref
    );
  }, [activeHref, sidebarSections]);
  const activateSection = useCallback(
    (sectionId: string) => {
      if (!sectionId) return;

      setActiveHref((current) => (current === sectionId ? current : sectionId));
    },
    []
  );

  const navigateToSection = useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement>,
      sectionId: string,
      parentSubmenuId = ""
    ) => {
      if (!sectionId) return;

      event.preventDefault();

      const contentRoot = mainContainerRef.current;
      const root = getPageScrollRoot();
      const target = document.getElementById(sectionId);

      if (!root || !contentRoot || !target || !contentRoot.contains(target)) {
        return;
      }

      activateSection(sectionId);
      pendingNavigationRef.current = sectionId;
      if (pendingNavigationTimeoutRef.current !== null) {
        window.clearTimeout(pendingNavigationTimeoutRef.current);
      }
      pendingNavigationTimeoutRef.current = window.setTimeout(() => {
        pendingNavigationRef.current = null;
        pendingNavigationTimeoutRef.current = null;
      }, 900);

      if (parentSubmenuId) {
        setExpandedSubmenus((current) => {
          if (current.has(parentSubmenuId)) return current;

          const next = new Set(current);
          next.add(parentSubmenuId);
          return next;
        });
      }

      const targetRect = target.getBoundingClientRect();
      window.scrollTo({
        top: window.scrollY + targetRect.top - getHeaderOffset() - 18,
        behavior: "smooth",
      });
      window.history.replaceState(null, "", `#${sectionId}`);
      setIsMobileNavigationOpen(false);
    },
    [activateSection]
  );

  const toggleSubmenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>, submenuId: string) => {
      event.preventDefault();

      setExpandedSubmenus((current) => {
        const next = new Set(current);

        if (next.has(submenuId)) {
          next.delete(submenuId);
        } else {
          next.add(submenuId);
        }

        return next;
      });
    },
    []
  );

  useEffect(() => {
    fetch(process.env.REACT_APP_PATH_HOME + "/markdown/homepage.md")
      .then((response) => response.text())
      .then((content) => {
        setMarkdownContent(content);
      })
      .catch((error) => console.error(error));
  }, []);

  useEffect(() => {
    return () => {
      if (scrollSpyAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollSpyAnimationFrameRef.current);
      }
      if (pendingNavigationTimeoutRef.current !== null) {
        window.clearTimeout(pendingNavigationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!activeHref) return;

    const activeNavItem = containerRef.current?.querySelector<HTMLElement>(
      `[data-nav-id="${activeHref}"]`
    );

    activeNavItem?.scrollIntoView({ block: "nearest" });
  }, [activeHref]);

  useEffect(() => {
    const contentRoot = mainContainerRef.current;
    const root = getPageScrollRoot();

    if (!root || !contentRoot || !markdownContent) return;

    const targets = sidebarSections
      .map(({ id }) => {
        const element = document.getElementById(id);

        return element instanceof HTMLElement && contentRoot.contains(element)
          ? element
          : null;
      })
      .filter((element): element is HTMLElement => Boolean(element));

    if (targets.length === 0) return;

    const updateActiveSection = () => {
      scrollSpyAnimationFrameRef.current = null;

      const activationLine =
        getHeaderOffset() + Math.min(window.innerHeight * 0.32, 180);
      let nextSectionId = targets[0].id;
      let closestTop = Number.NEGATIVE_INFINITY;

      if (pendingNavigationRef.current) {
        activateSection(pendingNavigationRef.current);
        setShowTopLink(window.scrollY > 240);
        return;
      }

      targets.forEach((target) => {
        const targetTop = target.getBoundingClientRect().top;

        if (targetTop <= activationLine && targetTop > closestTop) {
          closestTop = targetTop;
          nextSectionId = target.id;
        }
      });

      if (window.scrollY + window.innerHeight >= root.scrollHeight - 8) {
        nextSectionId = targets[targets.length - 1].id;
      }

      activateSection(nextSectionId);
      setShowTopLink(window.scrollY > 240);
    };

    const scheduleUpdate = () => {
      if (scrollSpyAnimationFrameRef.current !== null) return;

      scrollSpyAnimationFrameRef.current =
        window.requestAnimationFrame(updateActiveSection);
    };

    window.addEventListener("scroll", scheduleUpdate, { passive: true });

    const observer = new IntersectionObserver(scheduleUpdate, {
      root: null,
      rootMargin: `-${getHeaderOffset()}px 0px -68% 0px`,
      threshold: [0, 1],
    });

    targets.forEach((target) => observer.observe(target));
    scheduleUpdate();

    return () => {
      window.removeEventListener("scroll", scheduleUpdate);
      observer.disconnect();

      if (scrollSpyAnimationFrameRef.current !== null) {
        window.cancelAnimationFrame(scrollSpyAnimationFrameRef.current);
        scrollSpyAnimationFrameRef.current = null;
      }
    };
  }, [activateSection, markdownContent, sidebarSections]);

  const handleSidebarResizeStart = (
    event: React.PointerEvent<HTMLDivElement>
  ) => {
    event.preventDefault();

    if (isSidebarCollapsed(sidebarWidth)) {
      setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
      return;
    }

    setIsResizingSidebar(true);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    const updateWidth = (clientX: number) => {
      setSidebarWidth(clampSidebarWidth(clientX));
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      updateWidth(moveEvent.clientX);
    };

    const handlePointerEnd = () => {
      setIsResizingSidebar(false);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
    };

    updateWidth(event.clientX);
    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerEnd);
    window.addEventListener("pointercancel", handlePointerEnd);
  };

  const handleSidebarResizeKeyDown = (
    event: React.KeyboardEvent<HTMLDivElement>
  ) => {
    if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;

    event.preventDefault();
    setSidebarWidth((currentWidth) =>
      getNextSidebarKeyboardWidth(
        currentWidth,
        event.key === "ArrowRight" ? 16 : -16
      )
    );
  };

  const handleSidebarResizeReset = () => {
    setSidebarWidth(SIDEBAR_DEFAULT_WIDTH);
  };

  const renderedMarkdown = useMemo(
    () => (
      <ReactMarkdown
        rehypePlugins={[rehypeRaw]}
        remarkPlugins={[remarkGfm]}
        children={markdownContent}
        components={{
          h2: ({ node, ...props }) => {
            const children = props.children;

            return <h2 id={formatID(getHeadingText(children))}>{children}</h2>;
          },
        }}
      />
    ),
    [markdownContent]
  );

  useEffect(() => {
    const sidebar = sideNavRef.current;

    if (!sidebar) return;

    const updateLogoVisibility = () =>
      updateSidebarLogoVisibility(sidebar, `.${styles.sidebarLogoLink}`);
    const timeouts = [0, 250].map((delay) =>
      window.setTimeout(updateLogoVisibility, delay)
    );

    window.addEventListener("resize", updateLogoVisibility);

    return () => {
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      window.removeEventListener("resize", updateLogoVisibility);
    };
  }, [expandedSubmenus, isMobileNavigationOpen]);

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${
        isResizingSidebar ? styles.resizingSidebar : ""
      } ${isSidebarCollapsed(sidebarWidth) ? styles.collapsedSidebar : ""
      } ${isMobileNavigationOpen ? styles.mobileNavigationOpen : ""}`}
      style={
        {
          "--docs-sidebar-width": `${sidebarWidth}px`,
        } as React.CSSProperties
      }
    >
      <button
        type="button"
        className={`${styles.mobileNavigationToggle} ${
          isMobileNavigationToggleVisible
            ? styles.mobileNavigationToggleVisible
            : ""
        }`}
        aria-controls="home-navigation"
        aria-expanded={isMobileNavigationOpen}
        aria-label={isMobileNavigationOpen ? "Close navigation menu" : "Open navigation menu"}
        onPointerDown={(event) => {
          if (event.pointerType === "mouse") return;

          event.preventDefault();
          event.stopPropagation();
          setIsMobileNavigationOpen(true);
        }}
        onClick={() => setIsMobileNavigationOpen(true)}
      >
        <OrqIconContainer
          name="stack-burger-menu"
          type="none"
          size="dp24"
          color={EOrqColor.ActionMainEnabled}
        />
      </button>
      <button
        type="button"
        className={styles.mobileNavigationBackdrop}
        aria-label="Close navigation menu"
        onClick={() => setIsMobileNavigationOpen(false)}
      />
      <div className={styles.sidenav} id="home-navigation" ref={sideNavRef}>
        <div className={styles.sidebarNavContent}>
          <h4>BTG Solutions Data Services Home</h4>
          {sidebarItems.items.map((item) => {
            const itemId = getHrefId(item.href);
            const hasSubitems = item.subitems.length !== 0;
            const isExpanded = expandedSubmenus.has(itemId);
            const isActive =
              activeHref === itemId ||
              (hasSubitems && !isExpanded && activeParentId === itemId);

            return (
              <React.Fragment key={item.href}>
                <button
                  type="button"
                  className={`${styles.navItem} ${
                    isActive ? styles.active : ""
                  } ${hasSubitems ? styles.submenu : ""} ${
                    isExpanded ? styles.expanded : ""
                  }`}
                  data-nav-id={itemId}
                  aria-current={isActive ? "true" : undefined}
                  aria-expanded={hasSubitems ? isExpanded : undefined}
                  onClick={(event) => {
                    if (hasSubitems) {
                      toggleSubmenu(event, itemId);
                      return;
                    }

                    navigateToSection(event, itemId);
                  }}
                >
                  <span>{item.name}</span>
                  {hasSubitems && (
                    <svg
                      aria-hidden="true"
                      focusable="false"
                      data-prefix="fas"
                      data-icon="chevron-right"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 320 512"
                    >
                      {isExpanded ? (
                        <path
                          fill="currentColor"
                          d="M224 416c-8.188 0-16.38-3.125-22.62-9.375l-192-192c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0L224 338.8l169.4-169.4c12.5-12.5 32.75-12.5 45.25 0s12.5 32.75 0 45.25l-192 192C240.4 412.9 232.2 416 224 416z"
                        ></path>
                      ) : (
                        <path
                          fill="currentColor"
                          d="M96 480c-8.188 0-16.38-3.125-22.62-9.375c-12.5-12.5-12.5-32.75 0-45.25L242.8 256L73.38 86.63c-12.5-12.5-12.5-32.75 0-45.25s32.75-12.5 45.25 0l192 192c12.5 12.5 12.5 32.75 0 45.25l-192 192C112.4 476.9 104.2 480 96 480z"
                        ></path>
                      )}
                    </svg>
                  )}
                </button>
                {hasSubitems &&
                  item.subitems.map((subitem) => {
                    return (
                      <React.Fragment key={subitem.href}>
                        {isExpanded && (
                          <button
                            type="button"
                            className={`${styles.navItem} ${
                              activeHref === getHrefId(subitem.href)
                                ? styles.active
                                : ""
                            } ${styles.item_submenu}`}
                            data-nav-id={getHrefId(subitem.href)}
                            aria-current={
                              activeHref === getHrefId(subitem.href)
                                ? "true"
                                : undefined
                            }
                            onClick={(event) =>
                              navigateToSection(
                                event,
                                getHrefId(subitem.href),
                                itemId
                              )
                            }
                          >
                            <span>{subitem.name}</span>
                          </button>
                        )}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </div>
        <a
          className={styles.sidebarLogoLink}
          href={MAIN_SITE_URL}
          aria-label="Open BTG Pactual Data Services home"
        >
          <img
            className={styles.sidebarLogo}
            src={`${logoBasePath}/btg-dataservices.svg`}
            alt="BTG Pactual Data Services"
          />
        </a>
      </div>
      <div
        className={styles.sidebarResizeHandle}
        role="separator"
        aria-orientation="vertical"
        aria-valuemin={SIDEBAR_COLLAPSED_WIDTH}
        aria-valuemax={SIDEBAR_MAX_WIDTH}
        aria-valuenow={sidebarWidth}
        aria-valuetext={
          isSidebarCollapsed(sidebarWidth)
            ? "Collapsed"
            : `${Math.round(sidebarWidth)} pixels`
        }
        tabIndex={0}
        onPointerDown={handleSidebarResizeStart}
        onDoubleClick={handleSidebarResizeReset}
        onKeyDown={handleSidebarResizeKeyDown}
      >
        <span className={styles.sidebarResizeHandleIcon} aria-hidden="true">
          <OrqIconContainer
            name="chevron-right"
            type="none"
            size="dp24"
            color={EOrqColor.ActionMainEnabled}
          />
        </span>
      </div>
      <div className={styles.mainContainer} ref={mainContainerRef}>
        <div className={styles.wrapper}>
          {renderedMarkdown}
        </div>
      </div>
      <a
        className={showTopLink ? styles.top : styles.none}
        id="top"
        href="#readme-top"
      >
        <p>⬆</p>
      </a>
    </div>
  );
};

export default Home;
